/**
 * Canonical primary-navigation model — the single source of truth the
 * `Sidebar` renders from. Ordering, grouping, default collapse state, route,
 * and the exact `lucide-react` icon for every destination live here and
 * nowhere else.
 *
 * Routes are reconciled to the real route table (see
 * docs/handoff-notes/sidebar-audit.md). Icons are imported references so they
 * tree-shake and are type-checked.
 */
import {
  Banknote,
  Blocks,
  BookOpen,
  Briefcase,
  Building2,
  Calendar,
  CalendarClock,
  CalendarDays,
  ChartColumn,
  CircleUser,
  Clock,
  Coins,
  FileCheck,
  Filter,
  GitBranch,
  Layers,
  LayoutDashboard,
  ListChecks,
  type LucideIcon,
  MonitorSmartphone,
  Network,
  Receipt,
  Scale,
  ScanFace,
  ScrollText,
  ServerCog,
  Settings,
  SlidersHorizontal,
  Smartphone,
  Target,
  TrendingUp,
  UserCheck,
  Users,
  UserPlus,
  Workflow,
} from "lucide-react";

/** Toggleable (switchable) module codes that gate nav visibility. Must match
 *  the backend `apps.organization.models.ModuleCode` values. Bare-minimum
 *  modules (employees, leave, attendance, workflows, notifications, reports,
 *  events, ph_compliance) are always-on and never gate the nav. */
export type ModuleKey =
  | "lifecycle"
  | "compensation"
  | "payroll"
  | "expenses"
  | "recruitment"
  | "performance"
  | "accounting"
  | "biometric_attendance"
  | "copilot";

export interface NavLeaf {
  id: string;
  label: string;
  icon: LucideIcon;
  to: string;
  /** Match the index route exactly (NavLink `end`). */
  end?: boolean;
  /** Badge slot — counts are wired later; absent ⇒ no badge. */
  badgeKey?: string;
  requiresModule?: ModuleKey;
  requiresSuperAdmin?: boolean;
  requiresApprover?: boolean;
}

export interface NavSubgroup {
  kind: "subgroup";
  id: string;
  label: string;
  defaultOpen: boolean;
  requiresModule?: ModuleKey;
  items: NavLeaf[];
}

export interface NavSection {
  id: string;
  label: string;
  defaultOpen: boolean;
  requiresSuperAdmin?: boolean;
  items: Array<NavLeaf | NavSubgroup>;
}

export function isSubgroup(item: NavLeaf | NavSubgroup): item is NavSubgroup {
  return (item as NavSubgroup).kind === "subgroup";
}

// ---------------------------------------------------------------------------
// Pinned (top, no section header — always visible)
// ---------------------------------------------------------------------------
export const PINNED: NavLeaf[] = [
  { id: "dashboard", label: "Dashboard", icon: LayoutDashboard, to: "/", end: true },
  {
    id: "approvals",
    label: "Approvals",
    icon: ListChecks,
    to: "/approvals",
    badgeKey: "approvals",
    requiresApprover: true,
  },
  { id: "my-workspace", label: "My Workspace", icon: CircleUser, to: "/me", end: true },
];

// ---------------------------------------------------------------------------
// Sections
// ---------------------------------------------------------------------------
export const SECTIONS: NavSection[] = [
  {
    id: "people",
    label: "People",
    defaultOpen: true,
    items: [
      { id: "employees", label: "Employees", icon: Users, to: "/employees" },
      { id: "lifecycle", label: "Lifecycle", icon: GitBranch, to: "/lifecycle", requiresModule: "lifecycle" },
      { id: "organization", label: "Organization", icon: Network, to: "/org-settings" },
    ],
  },
  {
    id: "time",
    label: "Time & Attendance",
    defaultOpen: true,
    items: [
      { id: "attendance", label: "Attendance", icon: Clock, to: "/attendance" },
      { id: "leave", label: "Leave", icon: CalendarDays, to: "/leave-applications" },
      { id: "shifts", label: "Shifts", icon: CalendarClock, to: "/shifts" },
      { id: "calendar", label: "Calendar", icon: Calendar, to: "/calendar" },
      {
        kind: "subgroup",
        id: "biometrics",
        label: "Biometrics",
        defaultOpen: false,
        requiresModule: "biometric_attendance",
        items: [
          { id: "face-checkin", label: "Face Check-In", icon: Smartphone, to: "/me/attendance", end: true },
          {
            id: "checkin-review",
            label: "Check-In Review",
            icon: ScanFace,
            to: "/biometrics/review-queue",
            badgeKey: "checkinReview",
          },
          { id: "enrolment", label: "Enrolment", icon: UserPlus, to: "/biometrics/enrollments/new" },
          {
            id: "enrolment-review",
            label: "Enrolment Review",
            icon: UserCheck,
            to: "/biometrics/enrollment-review",
            badgeKey: "enrolmentReview",
          },
          { id: "devices", label: "Devices", icon: MonitorSmartphone, to: "/biometrics/devices" },
          { id: "provider-settings", label: "Provider Settings", icon: ServerCog, to: "/biometrics/settings" },
        ],
      },
    ],
  },
  {
    id: "pay",
    label: "Payroll & Pay",
    defaultOpen: true,
    items: [
      { id: "payroll-runs", label: "Payroll Runs", icon: Banknote, to: "/payroll-runs", requiresModule: "payroll" },
      { id: "payslips", label: "Payslips", icon: ScrollText, to: "/salary-slips", requiresModule: "payroll" },
      { id: "expenses", label: "Expenses", icon: Receipt, to: "/expenses", requiresModule: "expenses" },
      { id: "compensation", label: "Compensation", icon: Coins, to: "/compensation", requiresModule: "compensation" },
      { id: "accounting", label: "Accounting", icon: BookOpen, to: "/accounting", requiresModule: "accounting" },
      { id: "payroll-settings", label: "Payroll Settings", icon: SlidersHorizontal, to: "/payroll-settings", requiresModule: "payroll" },
    ],
  },
  {
    id: "talent",
    label: "Talent",
    defaultOpen: false,
    items: [
      { id: "hiring", label: "Hiring", icon: Briefcase, to: "/openings", requiresModule: "recruitment" },
      { id: "pipeline", label: "Pipeline", icon: Filter, to: "/applicants", requiresModule: "recruitment" },
      { id: "goals", label: "Goals", icon: Target, to: "/goals", requiresModule: "performance" },
      { id: "performance", label: "Performance", icon: TrendingUp, to: "/cycles", requiresModule: "performance" },
    ],
  },
  {
    id: "insights",
    label: "Insights & Compliance",
    defaultOpen: true,
    items: [
      { id: "reports", label: "Reports", icon: ChartColumn, to: "/reports" },
      { id: "report-builder", label: "Report Builder", icon: Blocks, to: "/report-builder" },
      { id: "ph-statutory", label: "PH Statutory", icon: Scale, to: "/ph-compliance" },
      { id: "statutory-exports", label: "Statutory Exports", icon: FileCheck, to: "/statutory-exports" },
    ],
  },
  {
    id: "admin",
    label: "Administration",
    defaultOpen: false,
    items: [
      { id: "workflow-admin", label: "Workflow Admin", icon: Workflow, to: "/workflow-admin" },
    ],
  },
  {
    id: "platform",
    label: "Platform · Dinnox-IT",
    defaultOpen: false,
    requiresSuperAdmin: true,
    items: [
      { id: "clients", label: "Clients", icon: Building2, to: "/admin/clients" },
      { id: "plans", label: "Plans", icon: Layers, to: "/admin/plans" },
    ],
  },
];

// ---------------------------------------------------------------------------
// Footer (pinned, separate from the scroll area)
// ---------------------------------------------------------------------------
export const FOOTER: NavLeaf[] = [
  { id: "settings", label: "Settings", icon: Settings, to: "/settings" },
];
